import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { api, BACKEND_URL } from '../api/axiosManager';

const parseDateInput = (dateInput) => {
  if (!dateInput) return null;
  try {
    if (dateInput instanceof Date) {
      return isNaN(dateInput.getTime()) ? null : dateInput;
    }
    if (typeof dateInput === 'string') {
      const dotNetMatch = dateInput.match(/\/Date\((\d+)\)\//);
      if (dotNetMatch) {
        const parsed = new Date(parseInt(dotNetMatch[1]));
        return isNaN(parsed.getTime()) ? null : parsed;
      }
      if (dateInput.includes('T') || dateInput.includes('-')) {
        const parsed = new Date(dateInput);
        return isNaN(parsed.getTime()) ? null : parsed;
      }
      const timestamp = dateInput.match(/\d+/)?.[0];
      if (timestamp) {
        const parsed = new Date(parseInt(timestamp));
        return isNaN(parsed.getTime()) ? null : parsed;
      }
      return null;
    }
    if (typeof dateInput === 'number') {
      const parsed = new Date(dateInput);
      return isNaN(parsed.getTime()) ? null : parsed;
    }
    return null;
  } catch {
    return null;
  }
};

// .NET tarih formatını dönüştür: /Date(1760426549750)/ -> 15.10.2024
// Ayrıca ISO string, Date objesi veya timestamp'i de handle eder
const formatDotNetDate = (dateInput) => {
  if (!dateInput) return '-';

  try {
    const date = parseDateInput(dateInput);
    if (!date) return '-';

    // Geçerli bir tarih mi kontrol et
    if (isNaN(date.getTime())) {
      return '-';
    }

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    return `${day}.${month}.${year}`;
  } catch (error) {
    return '-';
  }
};

// Kayıt tarihi KPI/activity ile uyumlu olması için UTC gün kırılımında gösterilir.
const formatDateUtcDay = (dateInput) => {
  const date = parseDateInput(dateInput);
  if (!date) return '-';
  const day = String(date.getUTCDate()).padStart(2, '0');
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const year = date.getUTCFullYear();
  return `${day}.${month}.${year}`;
};

const buildDisplayName = (user) => {
  // /api/admin/dashboard/users için ana kaynak: İsim + Soyisim
  const rawName = user.İsim || user.isim || '';
  const rawSurname = user.Soyisim || user.soyisim || '';

  const name = String(rawName).trim();
  const surname = String(rawSurname).trim();

  if (name && surname) {
    const normalizeForCompare = (value) =>
      String(value || '')
        .toLocaleLowerCase('tr-TR')
        .replace(/\s+/g, ' ')
        .trim();

    const normalizedName = normalizeForCompare(name);
    const normalizedSurname = normalizeForCompare(surname);

    // İsim zaten soyisimle bitiyorsa tekrar ekleme (örn: "Bilal Kantekin" + "KANTEKİN")
    if (
      normalizedSurname &&
      (normalizedName === normalizedSurname || normalizedName.endsWith(` ${normalizedSurname}`))
    ) {
      return name;
    }
    return `${name} ${surname}`.trim();
  }

  if (name) {
    return name;
  }
  if (surname) {
    return surname;
  }

  // Güvenli fallback (diğer endpoint formatları için)
  const firstName = user.firstName || user.FirstName || '';
  const lastName = user.lastName || user.LastName || '';
  const combined = `${firstName} ${lastName}`.trim();
  if (combined) return combined;

  const fullName = user.fullName || user.firstLastName || user.FirstLastName;
  if (fullName && String(fullName).trim()) {
    return String(fullName).trim();
  }

  return '-';
};

// Backend'den gelen veriyi frontend formatına çevir
const mapUserData = (user) => {
  const displayName = buildDisplayName(user);
  const rawName = (user.İsim || user.isim || '').toString().trim();
  const rawSurname = (user.Soyisim || user.soyisim || '').toString().trim();
  const fallbackParts = displayName.split(' ').filter(Boolean);
  const firstName =
    rawName ||
    user.firstName ||
    user.FirstName ||
    (fallbackParts.length > 0 ? fallbackParts[0] : '');
  const lastName =
    rawSurname ||
    user.lastName ||
    user.LastName ||
    (fallbackParts.length > 1 ? fallbackParts.slice(1).join(' ') : '');

  return {
  id: user.id || user.TCNo || user.tcNo, // ID yoksa TC No kullan
  tc: user.TCNo || user.tcNo || user.identityNum || '-',
  name: displayName,
  firstName: firstName || '',
  lastName: lastName || '',
  email: user.CommAddress || '-',
  phone: user.Telefon || '-',
  registerDate: formatDateUtcDay(user.KayitTarihi),
  loyaltyPoints: user.SadakatPuani || user.Puan || 0,
  segment: user.Segment || user.segment || '-',
  status: user.Durum || user.status || 'active',
  // Diğer alanlar
  address: user.Adres || user.address || '-',
  city: user.Sehir || user.city || '-',
  };
};

// Async Thunk - Kullanıcıları çek
export const fetchUsers = createAsyncThunk(
  'users/fetchUsers',
  async ({ force = false } = {}, { rejectWithValue, getState }) => {
    try {
      const state = getState().users;

      // Cache kontrolü
      if (!force && state.usersCache && state.usersCache.data && state.usersCache.data.length > 0) {
        return {
          data: state.usersCache.data,
          isCached: true
        };
      }

      const response = await api.get('/api/admin/dashboard/users');
      if (process.env.NODE_ENV === 'development') {
      }

      const mappedUsers = response.data.data?.map(mapUserData) || [];

      return {
        data: mappedUsers,
        isCached: false
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Kullanıcılar yüklenemedi');
    }
  }
  ,
  {
    condition: ({ force } = {}, { getState }) => {
      // Force modu varsa her zaman çek
      if (force) return true;

      const state = getState().users;
      const lastFetched = state.usersCache?.lastFetched;

      // Cache süresi (örneğin 2 dakika)
      const STALE_TIME = 2 * 60 * 1000;

      // Eğer son 2 dakika içinde çekildiyse ve veri varsa tekrar çekme
      if (lastFetched && Date.now() - lastFetched < STALE_TIME && state.usersCache?.data?.length > 0) {
        return false;
      }

      return true;
    },
  }
);

// Async Thunk - Kullanıcı listesinin tek bir sayfasını çek (sunucu taraflı sayfalama/arama)
// Backend: data en fazla `limit` kayıt (max 100), toplam `pagination.total` / `count` içinde gelir.
export const fetchUsersPage = createAsyncThunk(
  'users/fetchUsersPage',
  async ({ page = 1, limit = 50, search = '', dateFrom, dateTo } = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(limit) });
      const trimmedSearch = (search || '').trim();
      if (trimmedSearch) params.set('search', trimmedSearch);
      if (dateFrom) params.set('dateFrom', dateFrom);
      if (dateTo) params.set('dateTo', dateTo);

      const response = await api.get(`/api/admin/dashboard/users?${params.toString()}`);
      const body = response.data;

      if (!body || !Array.isArray(body.data)) {
        return rejectWithValue(body?.message || 'Kullanıcılar yüklenemedi');
      }

      const rows = body.data.map(mapUserData);
      const pagination = body.pagination || {};
      const total = Number(pagination.total ?? body.count ?? rows.length) || 0;
      const resolvedLimit = Number(pagination.limit) || limit;

      return {
        rows,
        total,
        page: Number(pagination.page) || page,
        limit: resolvedLimit,
        totalPages: Number(pagination.totalPages) || Math.max(1, Math.ceil(total / resolvedLimit)),
      };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Kullanıcılar yüklenemedi');
    }
  }
);

/**
 * Tekil kullanıcı endpoint'i ({ personalInfo, customerData, address }) formatını
 * mapUserData'nın tanıdığı düz objeye çevirir.
 */
const flattenUserDetailResponse = (data) => {
  if (!data || typeof data !== 'object') return data;

  // personalInfo varsa bu yeni detay formatı
  const pi = data.personalInfo;
  if (!pi) return data;

  return {
    TCNo: data.identityNum || pi.identityNum,
    İsim: pi.firstName || pi.firstLastName,
    Soyisim: pi.lastName,
    firstName: pi.firstName,
    lastName: pi.lastName,
    CommAddress: pi.email,
    Telefon: pi.tel,
    KayitTarihi: pi.kayitTarihi,
    SadakatPuani: pi.puan,
    Segment: pi.segment,
    Durum: pi.durum,
    // customerData ve address ayrıca cache'e de aktarılabilir
    _customerData: data.customerData || null,
    _address: data.address || null,
  };
};

// Async Thunk - Kullanıcı detayı çek
export const fetchUserById = createAsyncThunk(
  'users/fetchUserById',
  async (userId, { getState }) => {
    try {
      // Önce Redux store'dan kullanıcıyı bul
      const state = getState();
      const userFromStore = state.users.users.find(
        u => String(u.id) === String(userId) || String(u.tc) === String(userId)
      );

      if (userFromStore) {
        return userFromStore;
      }

      // Store'da yoksa API'den çek
      try {
      const response = await api.get(`/api/admin/dashboard/users/${userId}`);
        if (process.env.NODE_ENV === 'development') {
        }

        // Response formatını kontrol et
        if (response.data && response.data.data) {
      const raw = flattenUserDetailResponse(response.data.data);
      const mappedUser = mapUserData(raw);
      return mappedUser;
        } else if (response.data) {
          const raw = flattenUserDetailResponse(response.data);
          const mappedUser = mapUserData(raw);
          return mappedUser;
        } else {
          return { status: 404, message: 'Kullanıcı bulunamadı', user: null, userId };
        }
      } catch (apiError) {
        // 404 hatası için özel handling - promise'i reject etmeden döndür
        if (apiError.response?.status === 404) {
          return { 
            status: 404, 
            message: 'Kullanıcı bulunamadı',
            userId,
            user: null
          };
        }
        
        // Diğer hatalar için de null dön, uygulamayı bozmayalım
        return { 
          status: apiError.response?.status || 500,
          message: 'Kullanıcı detayı yüklenemedi',
          user: null,
          userId
        };
      }
    } catch (error) {
      // ✅ LOG: Beklenmeyen hata
      
      // Dış try-catch bloğu - beklenmeyen hatalar için
      return { 
        status: 500,
        message: 'Beklenmeyen bir hata oluştu',
        user: null,
        userId
      };
    }
  }
);

// Async Thunk - Kullanıcı oluştur
export const createUser = createAsyncThunk(
  'users/createUser',
  async (userData, { rejectWithValue }) => {
    try {
      // Request body hazırla
      const requestBody = {
        tcNo: userData.tcNo,
        gsm: userData.gsm,
        password: userData.password,
      };

      // Opsiyonel alanlar varsa ekle
      if (userData.firstName) {
        requestBody.firstName = userData.firstName;
      }
      if (userData.lastName) {
        requestBody.lastName = userData.lastName;
      }
      if (userData.email) {
        requestBody.email = userData.email;
      }

      const response = await api.post('/api/admin/dashboard/users', requestBody);

      if (response.data && response.data.success && response.data.data) {
        const user = response.data.data;

        // Backend'den gelen veriyi frontend formatına çevir
        // createdAt'i güvenli şekilde handle et
        let kayitTarihi = new Date();
        if (user.createdAt) {
          if (user.createdAt instanceof Date) {
            kayitTarihi = user.createdAt;
          } else if (typeof user.createdAt === 'string') {
            kayitTarihi = new Date(user.createdAt);
          } else if (typeof user.createdAt === 'number') {
            kayitTarihi = new Date(user.createdAt);
          }
        }

        const mappedUser = mapUserData({
          TCNo: user.tcNo,
          İsim: user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : (user.firstName || user.lastName || ''),
          CommAddress: user.email || '',
          Telefon: user.gsm || '',
          KayitTarihi: kayitTarihi,
          SadakatPuani: 0,
          Segment: '-',
          Durum: 'active',
        });

        return mappedUser;
      }

      return rejectWithValue('Kullanıcı oluşturulamadı');
    } catch (error) {
      const errorMessage = error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Kullanıcı oluşturulamadı';

      return rejectWithValue(errorMessage);
    }
  }
);

// Async Thunk - Kullanıcı sil
export const deleteUser = createAsyncThunk(
  'users/deleteUser',
  async (tcNo, { rejectWithValue }) => {
    try {
      await api.delete(`/api/admin/dashboard/users/${tcNo}`);
      return tcNo;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Kullanıcı silinemedi');
    }
  }
);

// Async Thunk - Kullanıcı bilgilerini güncelle
export const updateUserInfo = createAsyncThunk(
  'users/updateUserInfo',
  async (userData, { rejectWithValue }) => {
    try {
      const identityNum = (userData.tc || '').toString().trim();
      const tel = (userData.telefon || '').toString().trim();
      const firstName = (userData.firstName || '').toString().trim();
      const lastName = (userData.lastName || '').toString().trim();
      const email = (userData.email || '').toString().trim();

      // Yeni endpoint kuralı: identityNum + tel + firstName + lastName zorunlu, email opsiyonel
      if (!identityNum || !tel || !firstName || !lastName) {
        return rejectWithValue('TC, telefon, ad ve soyad zorunludur');
      }

      // Admin endpoint: PUT /api/admin/customers/personal-info
      const requestBody = {
        identityNum,
        tel,
        firstName,
        lastName,
      };
      if (email) {
        requestBody.email = email;
      }
      await api.put('/api/admin/customers/personal-info', requestBody);

      // Güncellenmiş kullanıcı verisini döndür
      return {
        tc: identityNum,
        firstName,
        lastName,
        name: `${firstName} ${lastName}`.trim(),
        phone: tel,
        email
      };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Kullanıcı bilgileri güncellenemedi');
    }
  }
);

// Async Thunk - Kullanıcı kartlarını çek
export const fetchUserCards = createAsyncThunk(
  'users/fetchUserCards',
  async (tcNo, { rejectWithValue }) => {
    try {
      // ✅ REST API endpoint kullanımı: GET /api/admin/customers/tc/:identityNumber
      const url = `/api/admin/customers/tc/${tcNo}`;
      
      const response = await api.get(url);

      // Response formatı kontrolü: { success: true, data: [...] } veya direkt array
      let cardsData = null;

      if (response.data) {
        // Eğer success wrapper varsa
        if (response.data.success && response.data.data) {
          cardsData = response.data.data;
        }
        // Direkt array ise
        else if (Array.isArray(response.data)) {
          cardsData = response.data;
        }
        // Eğer response.data bir obje ise ve içinde data varsa (geriye dönük uyumluluk)
        else if (response.data.data) {
          cardsData = response.data.data;
        } else if (response.data.Data) {
          cardsData = response.data.Data;
        }
      }

      // Array değilse boş array dön
      if (!Array.isArray(cardsData)) {
        return { tcNo, cards: [] };
      }

      // Backend'den gelen kart bilgilerini map et
      // ✅ Backend formatı: { MusteriKodu, Limit, KullanilabilirLimit, Bakiye, Magaza, ... }
      const cards = cardsData.map((card, index) => {
        // Kart numarası: MusteriKodu (yeni format) veya CurrAccCode (eski format)
        const cardNumber = card.MusteriKodu || card.musteriKodu || card.CurrAccCode || card.currAccCode || '-';
        
        // Mağaza/Ofis: Magaza (yeni format) veya OfficeDescription (eski format)
        const cardType = card.Magaza || card.magaza || card.OfficeDescription || card.officeDescription || '-';
        
        // Şehir: Magaza'dan ilk kelimeyi al (örn: "YENİ KOZA E-Ticaret" -> "YENİ")
        const city = cardType !== '-' ? cardType.split(' ')[0] : '-';
        
        // Bakiye: Bakiye (yeni format) veya null ise 0
        const balance = card.Bakiye !== null && card.Bakiye !== undefined 
          ? card.Bakiye 
          : (card.bakiye !== null && card.bakiye !== undefined ? card.bakiye : 0);
        
        // Limit: Limit (yeni format) veya CreditLimit (eski format)
        const limit = card.Limit || card.limit || card.CreditLimit || card.creditLimit || 0;
        
        // Kullanılabilir limit: KullanilabilirLimit (yeni format)
        const availableLimit = card.KullanilabilirLimit || card.kullanilabilirLimit || limit;
        
        return {
          id: index + 1,
          cardNumber,
          cardType,
          city,
          officeCode: card.OfficeCode || card.officeCode || '-',
          balance,
          limit,
          availableLimit, // ✅ Yeni alan: Kullanılabilir limit
          status: (card.Icra_Durum || card.icra_Durum) === 'İcrada Değil' ? 'active' : 'inactive',
          createdDate: formatDotNetDate(card.CreatedDate || card.createdDate),
          lastUsed: formatDotNetDate(card.CreatedDate || card.createdDate), // Şimdilik created date kullanıyoruz
          fullName: card.FirstLastName || card.firstLastName || '-',
        };
      });

      return { tcNo, cards };
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Kart bilgileri yüklenemedi');
    }
  }
);

// Async Thunk - Kullanıcı taksitlerini çek
export const fetchUserInstallments = createAsyncThunk(
  'users/fetchUserInstallments',
  async ({ currAccCode, limit = null, refresh = false }, { rejectWithValue }) => {
    try {
      // Admin endpoint: GET /api/admin/customers/:currAccCode/installments
      const params = new URLSearchParams();
      if (limit) {
        params.append('limit', limit);
      }
      if (refresh) {
        params.append('refresh', 'true');
      }

      const url = `/api/admin/customers/${currAccCode}/installments${params.toString() ? '?' + params.toString() : ''}`;

      const response = await api.get(url);


      if (response.data && response.data.success && response.data.data) {
        // Taksitleri formatla
        const installments = response.data.data.map(installment => ({
          ...installment,
          VadeTarihi: formatDotNetDate(installment.VadeTarihi || installment.DueDate),
          OdemeTarihi: formatDotNetDate(installment.OdemeTarihi || installment.PaymentDate),
        }));

        return { currAccCode, installments };
      } else {
        return { currAccCode, installments: [] };
      }
    } catch (error) {
      // 401 hatası için boş array dön (admin endpoint olduğu için logout yapılabilir)
      if (error.response?.status === 401) {
        return { currAccCode, installments: [] };
      }

      // 403 hatası için boş array dön (yetki yok)
      if (error.response?.status === 403) {
        return { currAccCode, installments: [] };
      }

      return rejectWithValue(error.response?.data || 'Taksit bilgileri yüklenemedi');
    }
  }
);

// Async Thunk - Taksit detaylarını çek
export const fetchInstallmentDetails = createAsyncThunk(
  'users/fetchInstallmentDetails',
  async ({ currAccCode, vadeTarihi }, { rejectWithValue }) => {
    try {
      // Admin endpoint: GET /api/admin/customers/:currAccCode/installments/:vadeTarihi/details
      const url = `/api/admin/customers/${currAccCode}/installments/${encodeURIComponent(vadeTarihi)}/details`;

      const response = await api.get(url);

      if (response.data && response.data.success && response.data.data) {
        const details = response.data.data;

        // Tarih formatlarını düzelt
        if (details.VadeTarihi || details.DueDate) {
          details.VadeTarihi = formatDotNetDate(details.VadeTarihi || details.DueDate);
        }
        if (details.OdemeTarihi || details.PaymentDate) {
          details.OdemeTarihi = formatDotNetDate(details.OdemeTarihi || details.PaymentDate);
        }

        return { currAccCode, vadeTarihi, details };
      } else {
        return rejectWithValue('Taksit detayları bulunamadı');
      }
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Taksit detayları yüklenemedi');
    }
  }
);

// Async Thunk - Kullanıcı adresini çek
export const fetchUserAddress = createAsyncThunk(
  'users/fetchUserAddress',
  async ({ identityNum }) => {
    try {
      // Admin endpoint: /api/admin/customers/address
      const response = await api.get('/api/admin/customers/address', {
        params: { identityNum }
      });

      if (response.data && response.data.success && response.data.data) {
        const addressData = response.data.data;

        // Adres objesinden string oluştur
        const addressParts = [
          addressData.QuarterName || addressData.MahalleAdi, // Sokak/Mahalle
          addressData.Address || addressData.Adres, // Adres detayı
          addressData.DistrictName || addressData.IlceAdi, // İlçe
          addressData.CityName || addressData.SehirAdi || addressData.IlAdi, // Şehir
        ].filter(Boolean); // Boş olanları filtrele

        if (addressParts.length > 0) {
          const addressString = addressParts.join(', ');
          return { identityNum, address: addressString };
        }
      }

      return { identityNum, address: null };
    } catch (error) {
      // Tüm hataları yakala ve null dön (uygulamayı bozmayalım)
      const status = error.response?.status;
      const errorData = error.response?.data;

      // 404 = Endpoint bulunamadı veya adres yok = normal durum
      if (status === 404 ||
        errorData?.error === 'Endpoint bulunamadı' ||
        errorData?.message?.includes('bulunamadı')) {
        return { identityNum, address: null };
      }

      // 401 hatası için null dön (admin endpoint olduğu için logout yapılabilir)
      if (status === 401) {
        return { identityNum, address: null };
      }

      // 403 hatası için null dön (yetki yok)
      if (status === 403) {
        return { identityNum, address: null };
      }

      // 500 hatası için null dön (backend hatası, uygulamayı bozmayalım)
      if (status === 500) {
        // Promise'i fulfilled olarak döndür, reject etme
        return { identityNum, address: null };
      }

      // Diğer hatalar için de null dön, uygulamayı bozmayalım
      // Promise'i fulfilled olarak döndür, reject etme
      return { identityNum, address: null };
    }
  }
);

// Async Thunk - Kullanıcı adresini güncelle
export const updateUserAddress = createAsyncThunk(
  'users/updateUserAddress',
  async ({ identityNum, addressData }, { rejectWithValue }) => {
    try {
      // Admin endpoint: PUT /api/admin/customers/address
      const response = await api.put('/api/admin/customers/address', {
        identityNum,
        ...addressData
      });

      if (response.data && response.data.success) {
        const updatedAddress = response.data.data;

        // Adres objesinden string oluştur
        const addressParts = [
          updatedAddress.QuarterName || updatedAddress.MahalleAdi,
          updatedAddress.Address || updatedAddress.Adres,
          updatedAddress.DistrictName || updatedAddress.IlceAdi,
          updatedAddress.CityName || updatedAddress.SehirAdi || updatedAddress.IlAdi,
        ].filter(Boolean);

        const addressString = addressParts.length > 0
          ? addressParts.join(', ')
          : null;

        return { identityNum, address: addressString };
      } else {
        return rejectWithValue('Adres güncellenemedi');
      }
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Adres güncellenemedi');
    }
  }
);

// Initial State
// Silinen kullanıcıyı ekrandaki liste sayfasından düşür (toplamı da günceller)
const removeFromUserListPage = (state, tcNo) => {
  const before = state.userList.rows.length;
  state.userList.rows = state.userList.rows.filter(
    (user) => String(user.tc) !== tcNo && String(user.id) !== tcNo
  );
  const removed = before - state.userList.rows.length;
  if (removed > 0) {
    state.userList.total = Math.max(0, state.userList.total - removed);
  }
};

const initialState = {
  users: [],
  selectedUser: null,
  // Her müşteri için ayrı cache (TC bazlı)
  userDetailsCache: {},
  // Ana liste cache
  usersCache: {
    data: [],
    lastFetched: null
  },
  // Müşteri listesi ekranı: yalnızca ekrandaki sayfa tutulur (sunucu taraflı sayfalama)
  userList: {
    rows: [],
    total: 0,
    page: 1,
    limit: 50,
    totalPages: 1,
    loading: false,
    loaded: false,
    error: null,
    requestId: null,
  },
  cardsLoading: false,
  installmentsLoading: false,
  addressLoading: false,
  loading: false,
  error: null,
  success: false,
};

// User Slice
const userSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = false;
    },
    setSelectedUser: (state, action) => {
      state.selectedUser = action.payload;
    },
    // Tüm cache'i temizle (gerekirse)
    clearAllCache: (state) => {
      state.userDetailsCache = {};
      state.usersCache = { data: [], lastFetched: null };
      state.cardsLoading = false;
      state.installmentsLoading = false;
      state.addressLoading = false;
    },
    // WebSocket event'inden kullanıcıyı state'den kaldır (fetchUsers çağırmadan)
    removeUserFromState: (state, action) => {
      const tcNo = String(action.payload);
      
      // Kullanıcıyı listeden kaldır
      state.users = state.users.filter(user => {
        const tcMatch = String(user.tc) === tcNo;
        const idMatch = String(user.id) === tcNo;
        return !tcMatch && !idMatch;
      });
      removeFromUserListPage(state, tcNo);

      // Cache'den de kaldır
      if (state.usersCache.data) {
        state.usersCache.data = state.usersCache.data.filter(user => {
          const tcMatch = String(user.tc) === tcNo;
          const idMatch = String(user.id) === tcNo;
          return !tcMatch && !idMatch;
        });
      }

      // Kullanıcının cache'ini temizle
      if (state.userDetailsCache[tcNo]) {
        delete state.userDetailsCache[tcNo];
      }
    },
  },
  extraReducers: (builder) => {
    // Fetch Users
    builder
      .addCase(fetchUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.users = action.payload.data;

        // Cache güncelle (eğer API'den geldiyse)
        if (!action.payload.isCached) {
          state.usersCache = {
            data: action.payload.data,
            lastFetched: Date.now()
          };
        }

        state.error = null;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Fetch Users Page (sunucu taraflı sayfalama)
    // Hızlı arama/sayfa geçişlerinde geç gelen eski yanıt yenisinin üzerine yazmasın diye requestId izlenir.
    builder
      .addCase(fetchUsersPage.pending, (state, action) => {
        state.userList.loading = true;
        state.userList.error = null;
        state.userList.requestId = action.meta.requestId;
      })
      .addCase(fetchUsersPage.fulfilled, (state, action) => {
        if (state.userList.requestId !== action.meta.requestId) return;
        state.userList = {
          ...action.payload,
          loading: false,
          loaded: true,
          error: null,
          requestId: null,
        };
      })
      .addCase(fetchUsersPage.rejected, (state, action) => {
        if (state.userList.requestId !== action.meta.requestId) return;
        state.userList.loading = false;
        state.userList.loaded = true;
        state.userList.error = action.payload || 'Kullanıcılar yüklenemedi';
        state.userList.requestId = null;
      });

    // Fetch User By ID
    builder
      .addCase(fetchUserById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserById.fulfilled, (state, action) => {
        state.loading = false;
        // 404 hatası durumunda selectedUser'ı temizle
        if (action.payload?.status === 404 || action.payload?.user === null) {
          state.selectedUser = null;
          state.error = { status: 404, message: action.payload?.message || 'Kullanıcı bulunamadı' };
        } else if (action.payload?.id || action.payload?.tc) {
          // Normal kullanıcı verisi geldiğinde (id veya tc varsa kullanıcı objesi)
        state.selectedUser = action.payload;
        state.error = null;
        } else {
          // Beklenmeyen format
          state.selectedUser = null;
          state.error = { status: 500, message: 'Beklenmeyen veri formatı' };
        }
      })
      .addCase(fetchUserById.rejected, (state, action) => {
        state.loading = false;
        // Rejected durumu artık olmamalı ama yine de handle edelim
        const errorStatus = action.payload?.response?.status || action.payload?.status || (typeof action.payload === 'string' && action.payload.includes('404') ? 404 : null);
        if (errorStatus === 404) {
          state.selectedUser = null;
        }
        state.error = action.payload;
      });

    // Create User
    builder
      .addCase(createUser.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(createUser.fulfilled, (state, action) => {
        state.loading = false;
        // Yeni kullanıcıyı listeye ekle
        state.users = [action.payload, ...state.users];

        // Cache'i güncelle
        if (state.usersCache.data) {
          state.usersCache.data = [action.payload, ...state.usersCache.data];
        }

        state.success = true;
        state.error = null;
      })
      .addCase(createUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      });

    // Delete User
    builder
      .addCase(deleteUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.loading = false;
        const tcNo = String(action.payload); // String'e çevir
        
        // Kullanıcıyı listeden kaldır (hem tc hem id kontrolü)
        state.users = state.users.filter(user => {
          const tcMatch = String(user.tc) === tcNo;
          const idMatch = String(user.id) === tcNo;
          return !tcMatch && !idMatch;
        });
        removeFromUserListPage(state, tcNo);

        // Cache'den de kaldır
        if (state.usersCache.data) {
          state.usersCache.data = state.usersCache.data.filter(user => {
            const tcMatch = String(user.tc) === tcNo;
            const idMatch = String(user.id) === tcNo;
            return !tcMatch && !idMatch;
          });
        }

        // Kullanıcının cache'ini temizle
        if (state.userDetailsCache[tcNo]) {
          delete state.userDetailsCache[tcNo];
        }
        state.success = true;
        state.error = null;
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Update User Info
    builder
      .addCase(updateUserInfo.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateUserInfo.fulfilled, (state, action) => {
        state.loading = false;
        const { tc, firstName, lastName, name, phone, email } = action.payload;
        const patch = { name, firstName, lastName, phone, email };

        // Kullanıcı listesindeki kullanıcıyı güncelle
        const userIndex = state.users.findIndex(user => String(user.tc) === String(tc));
        if (userIndex !== -1) {
          state.users[userIndex] = { ...state.users[userIndex], ...patch };
        }

        // Cache listesini de güncelle
        if (state.usersCache.data) {
          const cacheIndex = state.usersCache.data.findIndex(user => String(user.tc) === String(tc));
          if (cacheIndex !== -1) {
            state.usersCache.data[cacheIndex] = { ...state.usersCache.data[cacheIndex], ...patch };
          }
        }

        // selectedUser'ı güncelle
        if (state.selectedUser && String(state.selectedUser.tc) === String(tc)) {
          state.selectedUser = { ...state.selectedUser, ...patch };
        }

        state.success = true;
        state.error = null;
      })
      .addCase(updateUserInfo.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Fetch User Cards
    builder
      .addCase(fetchUserCards.pending, (state) => {
        state.cardsLoading = true;
        state.error = null;
      })
      .addCase(fetchUserCards.fulfilled, (state, action) => {
        state.cardsLoading = false;
        const { tcNo, cards } = action.payload;
        // TC bazlı cache'e kaydet
        if (!state.userDetailsCache[tcNo]) {
          state.userDetailsCache[tcNo] = {};
        }
        state.userDetailsCache[tcNo].cards = cards;
        state.error = null;
      })
      .addCase(fetchUserCards.rejected, (state, action) => {
        state.cardsLoading = false;
        state.error = action.payload;
      });

    // Fetch User Installments
    builder
      .addCase(fetchUserInstallments.pending, (state) => {
        state.installmentsLoading = true;
        state.error = null;
      })
      .addCase(fetchUserInstallments.fulfilled, (state, action) => {
        state.installmentsLoading = false;
        const { currAccCode, installments } = action.payload;
        // Kart numarasına (currAccCode) göre taksitleri kaydet
        const tcNo = Object.keys(state.userDetailsCache).find(tc =>
          state.userDetailsCache[tc]?.cards?.some(c => c.cardNumber === currAccCode)
        );
        if (tcNo && state.userDetailsCache[tcNo]) {
          // Installments'ı object olarak tut, her kart için ayrı
          if (!state.userDetailsCache[tcNo].installments) {
            state.userDetailsCache[tcNo].installments = {};
          }
          state.userDetailsCache[tcNo].installments[currAccCode] = installments;
        }
        state.error = null;
      })
      .addCase(fetchUserInstallments.rejected, (state, action) => {
        state.installmentsLoading = false;
        state.error = action.payload;
      });

    // Fetch Installment Details
    builder
      .addCase(fetchInstallmentDetails.pending, (state) => {
        state.installmentsLoading = true;
        state.error = null;
      })
      .addCase(fetchInstallmentDetails.fulfilled, (state, action) => {
        state.installmentsLoading = false;
        const { currAccCode, vadeTarihi, details } = action.payload;
        // Taksit detaylarını cache'e kaydet (gerekirse)
        const tcNo = Object.keys(state.userDetailsCache).find(tc =>
          state.userDetailsCache[tc]?.cards?.some(c => c.cardNumber === currAccCode)
        );
        if (tcNo && state.userDetailsCache[tcNo] && state.userDetailsCache[tcNo].installments) {
          // İlgili taksitin detaylarını güncelle
          const installments = state.userDetailsCache[tcNo].installments[currAccCode];
          if (Array.isArray(installments)) {
            const installmentIndex = installments.findIndex(t =>
              (t.VadeTarihi === vadeTarihi || t.DueDate === vadeTarihi)
            );
            if (installmentIndex !== -1) {
              state.userDetailsCache[tcNo].installments[currAccCode][installmentIndex] = {
                ...state.userDetailsCache[tcNo].installments[currAccCode][installmentIndex],
                ...details
              };
            }
          }
        }
        state.error = null;
      })
      .addCase(fetchInstallmentDetails.rejected, (state, action) => {
        state.installmentsLoading = false;
        state.error = action.payload;
      });

    // Fetch User Address
    builder
      .addCase(fetchUserAddress.pending, (state) => {
        state.addressLoading = true;
        state.error = null;
      })
      .addCase(fetchUserAddress.fulfilled, (state, action) => {
        state.addressLoading = false;
        const { identityNum, address } = action.payload;
        // TC numarasına (identityNum) göre cache'e kaydet
        if (state.userDetailsCache[identityNum]) {
          state.userDetailsCache[identityNum].address = address;
        }
        state.error = null;
      })
      .addCase(fetchUserAddress.rejected, (state, action) => {
        state.addressLoading = false;
        state.error = action.payload;
      });

    // Update User Address
    builder
      .addCase(updateUserAddress.pending, (state) => {
        state.addressLoading = true;
        state.error = null;
      })
      .addCase(updateUserAddress.fulfilled, (state, action) => {
        state.addressLoading = false;
        const { identityNum, address } = action.payload;

        // Cache'deki adresi güncelle
        if (state.userDetailsCache[identityNum]) {
          state.userDetailsCache[identityNum].address = address;
        }

        state.success = true;
        state.error = null;
      })
      .addCase(updateUserAddress.rejected, (state, action) => {
        state.addressLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, clearSuccess, setSelectedUser, clearAllCache, removeUserFromState } = userSlice.actions;
export default userSlice.reducer;

